import { Injectable } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ErrorModalComponent } from '../components/error-modal/error-modal.component';
import { isErrorLogPayload, StructuredLog, StructuredLogAttributes } from '../models/error-log';
import { CustomerService } from './customer.service';
import { SanitizeUtil } from './sanitize.util';
import { SentryLogService } from './sentry-log.service';
import { SnackbarService } from './snackbar.service';
import { UserService } from './user.service';
import { UtilityService } from './utility.service';

/** Longest log message kept; a call site's log text is a one-line summary, not a body. */
const MAX_LOG_MESSAGE_LENGTH = 1024;
/** Longest attribute value kept. An HTML error page or a stray object can run to tens of KB. */
const MAX_LOG_ATTRIBUTE_LENGTH = 8192;
const TRUNCATED_SUFFIX = '…[truncated]';
/** Deepest object flattened into dotted attributes (`error.error.detailedMsg`); deeper ones are serialised whole. */
const MAX_LOG_ATTRIBUTE_DEPTH = 3;
/**
 * An object with more keys than this is serialised as one attribute rather than flattened: an
 * error has a handful of fields, a request body such as a whole order can have hundreds.
 */
const MAX_FLATTENED_KEYS = 20;
/** Most attributes one log carries; values past it are counted in `portal.droppedAttributes`. */
const MAX_LOG_ATTRIBUTES = 64;
/** The message of a log whose call site passed something other than text as its log text. */
const DEFAULT_LOG_MESSAGE = 'ErrorService log';

@Injectable()
export class ErrorService {
	constructor(private _dialog: MatDialog,
				private _userService: UserService,
				private _customerService: CustomerService,
				private _snackbarService: SnackbarService,
				private _utilityService: UtilityService,
				private _sentryLog: SentryLogService) {
	}

	error(error, logText?: any, alertType?: string) {
		if (logText) {
			this.writeLog(error, logText);
		}

		if (alertType) {
			let title: string, message: string;

			if (error && error.error) {
				if (typeof error.error === 'string') {
					try {
						error = JSON.parse(error.error);
					} catch (e) {
						error = error.error;
					}
				} else {
					error = error.error;
				}
			}

			if (error && error.generalMsg) {
				title = error.generalMsg;
				message = error.detailedMsg || 'An unexpected error occurred. Please try again.';
			} else {
				title = 'Error';
				message = (error && error.message) ? error.message : 'An unexpected error occurred. Please try again.';
			}

			if (alertType === 'modal') {
				const dialogConfig = new MatDialogConfig();
				dialogConfig.autoFocus = false;
				dialogConfig.width = '600px';
				dialogConfig.maxWidth = '100vh';
				dialogConfig.data = {error: title, errorDetail: message};
				return this._dialog.open(ErrorModalComponent, dialogConfig);
			} else if (alertType === 'toast') {
				this._snackbarService.openSnackBar(message, 'Close', null);
			}
		}
	}

	/**
	 * A structured log in Sentry Logs, never an error event: most call sites report a handled
	 * failure, and an event per failure would use up the error quota and fire alerts. Never throws,
	 * so the modal error() opens next is never lost to a payload that cannot be logged.
	 */
	private writeLog(error: unknown, logText: unknown): void {
		try {
			// Credentials and card data go first, while Errors are still readable; then base64 data, as always.
			const payload = this._utilityService.copyAndRemoveBase64Data(SanitizeUtil.toSafeJson({text: logText, error}));
			const attributes: StructuredLogAttributes = {};
			this.addUserAttributes(attributes);
			const {message} = this.toStructuredLog(payload, attributes);
			attributes['portal.source'] = 'ErrorService.error';
			this._sentryLog.write('error', message, attributes);
		} catch {
			// Logging must never stop the modal or the toast.
		}
	}

	/**
	 * Maps a payload onto Sentry Logs' message + attributes: a string `text` becomes the message,
	 * every other key becomes attributes (see addAttribute). Values are serialised here rather than
	 * by the SDK, which sends whatever size it is given.
	 */
	private toStructuredLog(payload: unknown, attributes: StructuredLogAttributes): StructuredLog {
		if (!isErrorLogPayload(payload)) {
			return {message: DEFAULT_LOG_MESSAGE, attributes};
		}

		const {text, ...rest} = payload;
		const hasTextMessage = typeof text === 'string' && text.length > 0;
		const message = hasTextMessage ? SanitizeUtil.redactCredentialText(text) : DEFAULT_LOG_MESSAGE;
		if (!hasTextMessage && text !== undefined) {
			rest['text'] = text;
		}
		let dropped = 0;
		for (const [key, value] of Object.entries(rest)) {
			dropped += this.addAttribute(attributes, key, value, 0);
		}
		if (dropped > 0) {
			attributes['portal.droppedAttributes'] = dropped;
		}

		return {message: this.truncate(message, MAX_LOG_MESSAGE_LENGTH), attributes};
	}

	/**
	 * Adds `value` under `key`, flattening an object into one attribute per field (`error.status`,
	 * `error.url`, `error.error.detailedMsg`). Sentry's server-side scrubber replaces a whole
	 * attribute whose text mentions auth, password or token (a 401's "Unauthorized", a failed
	 * sign-in's server message), so one attribute per field keeps the rest of the error readable.
	 * Credential keys are dropped at every level. Returns how many values did not fit in the budget.
	 */
	private addAttribute(attributes: StructuredLogAttributes, key: string, value: unknown, depth: number): number {
		if (SanitizeUtil.isCredentialKey(key) || value === undefined || value === null) {
			return 0;
		}
		if (this.isFlattenable(value, depth)) {
			let dropped = 0;
			for (const [childKey, child] of Object.entries(value)) {
				dropped += this.addAttribute(attributes, `${key}.${childKey}`, child, depth + 1);
			}
			return dropped;
		}
		if (Object.keys(attributes).length >= MAX_LOG_ATTRIBUTES) {
			return 1;
		}
		const attribute = this.toAttributeValue(value);
		if (attribute !== undefined) {
			attributes[key] = attribute;
		}
		return 0;
	}

	/** A plain record small and shallow enough to be worth one attribute per field. */
	private isFlattenable(value: unknown, depth: number): value is Record<string, unknown> {
		return typeof value === 'object' && value !== null && !Array.isArray(value)
			&& depth < MAX_LOG_ATTRIBUTE_DEPTH && Object.keys(value).length <= MAX_FLATTENED_KEYS;
	}

	/**
	 * Who it was: the user's name and client, and the selected customer's id and name. The stored
	 * user also carries the session token and the customer record dozens of fields, so nothing else
	 * is taken. A stored record that does not parse costs these attributes, not the log.
	 */
	private addUserAttributes(attributes: StructuredLogAttributes): void {
		try {
			const user = this._userService.getUser();
			this.addIdentity(attributes, 'user.username', user?.username);
			this.addIdentity(attributes, 'user.clientId', user?.cxtId);
			const customer = this._customerService.getSelectedCustomer();
			this.addIdentity(attributes, 'customer.id', customer?.id);
			this.addIdentity(attributes, 'customer.name', customer?.customerName);
		} catch {
			// A corrupt stored user or customer is no reason to lose the log.
		}
	}

	private addIdentity(attributes: StructuredLogAttributes, key: string, value: unknown): void {
		if (typeof value === 'string' || typeof value === 'number') {
			attributes[key] = value;
		}
	}

	private toAttributeValue(value: unknown): string | number | boolean | undefined {
		if (typeof value === 'number') {
			return SanitizeUtil.redactCardNumber(value);
		}
		if (typeof value === 'boolean') {
			return value;
		}
		if (typeof value === 'string') {
			return this.truncate(SanitizeUtil.redactCredentialText(value), MAX_LOG_ATTRIBUTE_LENGTH);
		}
		let serialized: string | undefined;
		try {
			serialized = JSON.stringify(value, SanitizeUtil.credentialSafeReplacer);
		} catch {
			serialized = '[unserializable]';
		}
		return serialized === undefined ? undefined : this.truncate(serialized, MAX_LOG_ATTRIBUTE_LENGTH);
	}

	private truncate(value: string, maxLength: number): string {
		return value.length > maxLength ? value.slice(0, maxLength) + TRUNCATED_SUFFIX : value;
	}
}
